import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import {
  fetchConfigOccupation,
  fetchConfigPurpose,
  fetchConfigPurposeOfBusiness,
  fetchConfigRelationship,
  fetchConfigRelationshipOfBusiness,
  fetchConfigSourcesOfWealth,
} from 'config';
import { DESKTOP_MIN_WIDTH } from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import useFormInput from 'hooks/form/useFormInput';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import useScrollIntoView from 'hooks/useScrollIntoView';
import useStore from 'hooks/useStore';
import { isEmpty, isFunction, isString } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { paragraph500Mixin } from 'styles';
import { arrayUtils, stringUtils } from 'utils';
import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from '../../DropdownCommon';
import { checkDomainAllowFetchDataConfig } from '../../func';
import withAutoScaleToFit from '../../withAutoScaleToFit';

const CustomSearchDropdown = ({
  ref,
  field,
  isConfigRequired,
  data,
  keyFind,
  keyTranslate,
  keyProp,
  noResultText,
  placeholder,
  information,
  setInformation,
  validation,
  setValidation,
  onChange,
  onClick,
  isDisabled,
  isSearchHidden,
  isSkipSearchValidationOnVi = false,
  styles = {},
  dropdownStyles = {},
  inputStyles = {},
  toggleStyles = {},
  toggleTextStyles = {},
  dropdownMenuStyles = {},
  dropdownMenuContainerStyles = {},
  dropdownMenuItemStyles = {},
  arrowDownIconColor = '',
  isClearBoth = false,
  scrollUp = false,
  isScalable = false,
  dropdownItemPrefix = '',
}) => {
  const { t, locate, isVi } = useLang();
  const { state } = useStore();
  const {
    configOccupation,
    configRelationship,
    configRelationshipOfBusiness,
    configPurpose,
    configPurposeOfBusiness,
    configSourcesOfWealth,
  } = state || {};

  const [searchValue, setSearchValue] = useState('');
  const [dataSearch, setDataSearch] = useState(null);
  const [currentSelected, setCurrentSelected] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isSearchInvalid =
    !(isSkipSearchValidationOnVi && isVi) && !searchTextValid(searchValue);

  const { setInformationFormInput, FormLabel } = useFormInput(field);

  const SEARCH_NAME_FIELD = `search-${field.name}`;
  const NO_RESULT_TEXT =
    t(noResultText) ||
    stringUtils.replaceKeyword(t('form_text_no_result_by_label'), [
      { key: 'label', value: t(field.label).toLowerCase() },
    ]);

  const isShowArrowDownIcon = !isDisabled && data?.length > 1;

  const validRef = useRef(null);
  useScrollIntoView(validRef, validation[field.name], [validation]);

  useInputFocus(SEARCH_NAME_FIELD);

  useEffect(() => {
    if (!checkDomainAllowFetchDataConfig()) return;

    if (!configOccupation) {
      fetchConfigOccupation(locate);
    }

    if (!configRelationship) {
      fetchConfigRelationship(locate);
    }

    if (!configRelationshipOfBusiness) {
      fetchConfigRelationshipOfBusiness(locate);
    }

    if (!configPurpose) {
      fetchConfigPurpose(locate);
    }

    if (!configPurposeOfBusiness) {
      fetchConfigPurposeOfBusiness(locate);
    }

    if (!configSourcesOfWealth) {
      fetchConfigSourcesOfWealth(locate);
    }
  }, [
    configOccupation,
    configRelationship,
    configRelationshipOfBusiness,
    configPurpose,
    configPurposeOfBusiness,
    configSourcesOfWealth,
  ]);

  useEffect(() => {
    if (isEmpty(data)) return;
    const item =
      data?.find(
        (c) =>
          (Boolean(
            isString(c?.[keyFind]) &&
              isString(information[field.name]?.[keyFind])
          ) &&
            c?.[keyFind]?.trim() ===
              information[field.name]?.[keyFind]?.trim()) ||
          (Boolean(
            isString(c?.[keyFind]) && isString(information[field.name])
          ) &&
            c?.[keyFind]?.trim() === information[field.name]?.trim()) ||
          (Boolean(
            isString(c?.[keyFind]) && isString(information[field.name])
          ) &&
            c?.[keyFind]?.trim() ===
              `${dropdownItemPrefix} ${information[field.name]?.trim()}`)
      ) || null;
    setCurrentSelected(item);
  }, [information[field.name], data]);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setDataSearch(null);

      return;
    }

    const newDataSearch = data?.filter(
      (item) =>
        (item?.[keyTranslate]?.toLowerCase()?.includes(value?.toLowerCase()) ||
          item?.[keyFind]?.toLowerCase()?.includes(value?.toLowerCase())) &&
        item
    );
    setDataSearch(newDataSearch);
  };

  const handleItemSelected = (item) => {
    setCurrentSelected(item);

    setSearchValue('');

    setDataSearch(null);

    setShowDropdown(false);

    const newInformation = {
      ...information,
      [field.name]: keyProp ? item?.[keyProp] : item,
    };
    setInformationFormInput({
      value: item,
      information: newInformation,
      setInformation,
    });

    setValidation((prev) => ({ ...prev, [field.name]: '' }));

    onChange && onChange(item);
  };

  const handleDropdownOnToggle = () => {
    if (isDisabled) {
      setShowDropdown(false);
      return;
    }
    if (isFunction(onClick)) {
      setShowDropdown(false);
      return;
    }

    setSearchValue('');

    setDataSearch(null);

    setShowDropdown(!isShowDropdown);

    setValidation((prev) => ({ ...prev, [field.name]: '' }));

    if (scrollUp) {
      window.scrollBy({ top: 300, behavior: 'smooth' });
    }
  };

  const handleDropdownOnClick = () => {
    isFunction(onClick) && onClick();
  };

  const renderDropdownToggleChildren = () => {
    const text =
      t(currentSelected?.[keyTranslate]) ||
      t(currentSelected?.[keyFind]) ||
      t(field.placeholder);

    return (
      <>
        {isScalable ? (
          <TextScale id={text} name={field.name}>
            {text}
          </TextScale>
        ) : (
          <Text
            name={field.name}
            style={toggleTextStyles}
            $isPlaceholder={isEmpty(currentSelected)}
          >
            {text}
          </Text>
        )}
        <ArrowDownIcon
          color={arrowDownIconColor || 'var(--ds-c-blue-hyperlink-default)'}
          size={{ width: 12, height: 7 }}
          styles={{
            transform: isShowDropdown && 'rotate(180deg)',
            display: !isShowArrowDownIcon && 'none',
          }}
        />
      </>
    );
  };
  const renderDropdownMenuChildren = () => {
    return (
      <>
        {data?.length > 1 && (
          <>
            {!isSearchHidden && (
              <DropdownSearchInput
                ref={ref}
                name={SEARCH_NAME_FIELD}
                value={searchValue || ''}
                placeholder={t(
                  placeholder || 'form_placeholder_type_to_search'
                )}
                onChange={handleSearch}
                style={{ ...inputStyles }}
              />
            )}
            <DropdownMenuContainer
              style={{
                borderRadius: isSearchHidden ? '8px' : '0 0 8px 8px',
                transform: isSearchHidden && 'translate(0px, 52px)',
                paddingBottom: 0,
                ...dropdownMenuContainerStyles,
              }}
            >
              {renderDropdownItems(isSearchInvalid ? [] : dataSearch || data)}
            </DropdownMenuContainer>
          </>
        )}
      </>
    );
  };
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          key={0}
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
          style={dropdownMenuItemStyles}
        >
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{NO_RESULT_TEXT}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={index}
            eventKey={index}
            onClick={() => handleItemSelected(item)}
            style={{ textTransform: 'uppercase', ...dropdownMenuItemStyles }}
          >
            {t(item?.[keyTranslate]) || t(item?.[keyFind])}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  const getStyleDropdown = () => {
    const common = { ...dropdownStyles };
    return common;
  };
  const getStyleDropdownToggle = () => {
    const common = {
      paddingRight: isShowArrowDownIcon ? '32px' : '10px',
      ...toggleStyles,
    };
    return common;
  };
  const getStyleDropdownMenu = () => {
    const common = {
      boxShadow: isSearchHidden && 'none',
      transform: isSearchHidden && 'translate(0px, 0px)',
      zIndex: isSearchHidden && 1,
      minHeight: '50px',
      ...dropdownMenuStyles,
    };
    return common;
  };

  return (
    <CustomSearchDropdownStyled
      ref={validRef}
      style={{ ...styles }}
      $isClearBoth={isClearBoth}
    >
      <FormLabel
        isConfigRequired={isConfigRequired}
        styles={{ marginBottom: '4px' }}
      />
      <DropdownCommon
        placeholder={field?.placeholder}
        keyError={validation?.[field?.name]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        onClick={handleDropdownOnClick}
        isDisabled={isDisabled}
        renderDropdownToggleChildren={renderDropdownToggleChildren}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
        styleDropdown={getStyleDropdown()}
        styleDropdownToggle={getStyleDropdownToggle()}
        styleDropdownMenu={getStyleDropdownMenu()}
      />
    </CustomSearchDropdownStyled>
  );
};

const CustomSearchDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    ${(props) =>
      props.$isClearBoth &&
      `
        clear: both;
      `}
  }
`;

const NoItemText = styled.p`
  ${paragraph500Mixin};
  display: flex;
  align-items: center;
  color: var(--ds-c-blue-disabled);
  text-transform: initial;
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

const Text = styled.p`
  ${paragraph500Mixin};
  color: ${(props) =>
    props.$isPlaceholder ? 'var(--ds-c-blue-disabled)' : 'var(--c-primary)'};

  &:hover {
    opacity: 0.7;
  }
`;
const TextScale = withAutoScaleToFit(Text);

export default CustomSearchDropdown;
